import {AuthUserPermissions} from '@/features/auth/types'
import {TKey} from '@/translations/i18n'
import {ExportEntity, EXPORT_ENTITIES} from '@/features/exports/types'

/**
 * Static descriptor for every export entity that backs the sidebar tabs.
 *
 * `permission` maps the entity to the existing feature permission that already gates
 * its export action (the Patients export today requires `patients.write`). The sidebar
 * toggle and the tab list are filtered through this selector (FR-002a / FR-003).
 */
export interface ExportEntityDescriptor {
    key: ExportEntity
    labelKey: TKey
    permission: (permissions: AuthUserPermissions | undefined) => boolean
}

const hasWrite =
    (feature: keyof AuthUserPermissions) =>
    (permissions: AuthUserPermissions | undefined): boolean =>
        !!permissions?.[feature]?.write

export const EXPORT_ENTITY_DESCRIPTORS: ExportEntityDescriptor[] = [
    {key: 'patients', labelKey: 'exports:entity_patients', permission: hasWrite('patients')},
    {key: 'patient_events', labelKey: 'exports:entity_patient_events', permission: hasWrite('patients')},
    {key: 'treatment_requests', labelKey: 'exports:entity_treatment_requests', permission: hasWrite('requests')},
    {key: 'projects', labelKey: 'exports:entity_projects', permission: hasWrite('projects')},
    {key: 'accesses_raw', labelKey: 'exports:entity_accesses_raw', permission: hasWrite('accesses')},
    {key: 'accesses_report', labelKey: 'exports:entity_accesses_report', permission: hasWrite('accesses')},
    {key: 'accesses_ria', labelKey: 'exports:entity_accesses_ria', permission: hasWrite('accesses')},
    {key: 'users', labelKey: 'exports:entity_users', permission: hasWrite('users')}
]

const DESCRIPTOR_BY_KEY = new Map<string, ExportEntityDescriptor>(
    EXPORT_ENTITY_DESCRIPTORS.map(descriptor => [descriptor.key, descriptor])
)

/** Descriptors the user is allowed to see, preserving the canonical order. */
export const getPermittedEntityDescriptors = (permissions: AuthUserPermissions | undefined): ExportEntityDescriptor[] =>
    EXPORT_ENTITY_DESCRIPTORS.filter(descriptor => descriptor.permission(permissions))

/** True when the user can access the export sidebar at all (holds ≥1 entity export permission). */
export const hasAnyExportPermission = (permissions: AuthUserPermissions | undefined): boolean =>
    getPermittedEntityDescriptors(permissions).length > 0

/** i18n key for an entity label, tolerant of unknown/unaligned backend entity values. */
export const getEntityLabelKey = (entity: string): TKey =>
    DESCRIPTOR_BY_KEY.get(entity)?.labelKey ?? 'exports:entity_unknown'

export const isKnownExportEntity = (entity: string): entity is ExportEntity =>
    (EXPORT_ENTITIES as readonly string[]).includes(entity)
